import React, { Fragment } from 'react';
import jamespot from 'jamespot-user-api';
import type { DisplayInputComponentProps } from '../../types';
import type { jObjectLittle, WidgetTaxonomy } from 'jamespot-user-api';
import { FieldValues, Path, useController } from 'react-hook-form';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';

type InputTaxonomyConfig<T extends FieldValues = FieldValues> = {
    name: Path<T>;
    placeholder: string;
    type: string;
    limit?: number;
};

const autocompleteTaxonomyList = (
    inputValue: string,
    params: { idTaxonomy?: string; type?: string },
    page = 1,
    limit = 8,
) => {
    const payload = {
        query: inputValue,
        queryFields: ['title'],
        limit: limit,
        page: page,
        format: 'autocomplete',
        additionalFilter: {},
        filters: [],
        orders: [{ name: 'title', sort: 'ASC' }],
        security: 1,
        uriTaxonomy: `${params?.type || ''}/${params?.idTaxonomy || ''}`,
    };
    return jamespot.taxonomy.autocompleteList(payload).then((response) => response.result);
};

const autocompleteTaxonomyHierarchical = (
    uriConcept: string,
    params: { idTaxonomy?: string; type?: string },
    limit = 8,
) => {
    if (uriConcept === undefined) return Promise.resolve([]);
    const payload = {
        limit: limit,
        page: 1,
        format: 'autocomplete',
        additionalFilter: {},
        filters: [],
        orders: [{ name: 'title', sort: 'ASC' }],
        security: 1,
        uriTaxonomy: `${params?.type || ''}/${params?.idTaxonomy || ''}`,
        ...(!!uriConcept && { uriConcept: uriConcept }),
    };
    return jamespot.taxonomy.autocompleteHierarchy(payload).then((response) => response.result);
};

export const InputTaxonomy =
    <TFieldValues extends FieldValues>(widget: WidgetTaxonomy, config: InputTaxonomyConfig<TFieldValues>) =>
    // eslint-disable-next-line react/display-name
    (props: DisplayInputComponentProps<TFieldValues> & { displayModal?: boolean }) => {
        const InputSelectList = commonComponentsRegistry.getLazyComponent('InputSelectList');
        const InputSelectHierarchicalTree = commonComponentsRegistry.getLazyComponent('InputSelectHierarchicalTree');

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { field } = useController(props);

        const htmlFor = `displayer-field-${config.name}`;
        const getOptionValue = (option: jObjectLittle) => `${option.title}`;

        return (
            <Fragment>
                {widget.params?.type === 'taxonomySkos' ? (
                    <InputSelectHierarchicalTree
                        {...props}
                        {...config}
                        searchable
                        multiple
                        htmlFor={htmlFor}
                        getLabel={getOptionValue}
                        getValue={getOptionValue}
                        getSearch={getOptionValue}
                        asyncPromise={(inputValue: string) => autocompleteTaxonomyList(inputValue, widget.params)}
                        hierarchicalTaxonomyPromise={(uriConcept: string) =>
                            autocompleteTaxonomyHierarchical(uriConcept, widget.params)
                        }
                    />
                ) : (
                    <InputSelectList<TFieldValues>
                        {...props}
                        {...config}
                        onChange={field.onChange}
                        searchable
                        multiple
                        htmlFor={htmlFor}
                        getLabel={getOptionValue}
                        getValue={getOptionValue}
                        getSearch={getOptionValue}
                        asyncPromise={(inputValue: string, page?: number) =>
                            autocompleteTaxonomyList(inputValue, widget.params, page)
                        }
                        allowCustomOptions={widget.params.type === 'taxonomyOpen'}
                        rules={{ required: props.mandatory }}
                    />
                )}
            </Fragment>
        );
    };
